/**
 * Campaign Lookup Editor — used for both /campaigns/lookups/new
 * (create) and /campaigns/lookups/:id (edit).
 *
 * Create mode: minimal form to set name, description, and one
 *   key-column-only schema. Save → redirect to edit mode at the new id.
 *
 * Edit mode: full table editor — inline cell editing, add/delete rows,
 *   star icon for default, CSV upload, CSV download, column rename.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  StarIcon as StarOutline,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarFilled } from "@heroicons/react/24/solid";
import LoadingSpinner from "../components/common/LoadingSpinner";
import LookupCsvUploadModal from "../components/campaigns/lookups/LookupCsvUploadModal";
import { useToast } from "../components/ui/Toast";
import {
  getLookup,
  getLookupRows,
  createLookup,
  updateLookup,
  insertLookupRow,
  updateLookupRow,
  deleteLookupRow,
  setLookupDefaultRow,
  renameLookupColumn,
  changeLookupKeyColumn,
} from "../services/lookups.service";
import { CONTACT_FIELDS } from "../services/templates.service";
import type {
  CampaignLookup,
  CampaignLookupRow,
  LookupColumn,
} from "../types/lookup.types";

interface EditorProps {
  mode: "create" | "edit";
}

export default function CampaignLookupEditor({ mode }: EditorProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  if (mode === "create") {
    return (
      <CreateForm
        onCreated={(newId) => navigate(`/campaigns/lookups/${newId}`)}
        onCancel={() => navigate("/campaigns/lookups")}
      />
    );
  }

  if (!id) {
    return null;
  }
  return <EditForm lookupId={id} navigate={navigate} qc={qc} />;
}

// ============================================================================
// Create form (minimal — name + key column, then redirect to edit)
// ============================================================================

function CreateForm({
  onCreated,
  onCancel,
}: {
  onCreated: (newId: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [keyColumn, setKeyColumn] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      if (!name.trim()) {
        throw new Error("Name is required");
      }
      if (!keyColumn.trim()) {
        throw new Error("Key column is required");
      }
      return createLookup({
        name: name.trim(),
        description: description.trim() || null,
        key_column: keyColumn.trim(),
        column_schema: [{ name: keyColumn.trim(), role: "key", order: 0 }],
      });
    },
    onSuccess: (lookup) => onCreated(lookup.id),
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="p-8 max-w-2xl">
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to Lookups
      </button>

      <h1 className="text-2xl font-bold mb-2">New Lookup</h1>
      <p className="text-sm text-gray-600 mb-6">
        Pick a name and the column that will be used to match contacts. After
        creating, you can add more columns and rows.
      </p>

      <div className="space-y-4 bg-white border border-gray-200 rounded-md p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Books by State"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's this lookup for?"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key column <span className="text-red-500">*</span>
          </label>
          <select
            value={keyColumn}
            onChange={(e) => setKeyColumn(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Select a contact field…</option>
            {CONTACT_FIELDS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} ({f.value})
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            The contact attribute this lookup matches against at send time.
            Restricted to mappable contact fields to prevent typos.
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={create.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {create.isPending ? "Creating…" : "Create Lookup"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Edit form (full table editor)
// ============================================================================

function EditForm({
  lookupId,
  navigate,
  qc,
}: {
  lookupId: string;
  navigate: ReturnType<typeof useNavigate>;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [search, setSearch] = useState("");
  const [showCsv, setShowCsv] = useState(false);
  const [editingHeader, setEditingHeader] = useState<string | null>(null);

  const lookupQ = useQuery({
    queryKey: ["campaign-lookup", lookupId],
    queryFn: () => getLookup(lookupId),
  });
  const rowsQ = useQuery({
    queryKey: ["campaign-lookup-rows", lookupId],
    queryFn: () => getLookupRows(lookupId),
  });

  const lookup = lookupQ.data;
  const rows = useMemo(() => rowsQ.data ?? [], [rowsQ.data]);

  // Derive ordered column names from the lookup's column_schema. Fall
  // back to scanning the row data jsonb if the schema is empty (e.g.,
  // first row added before the schema was populated).
  const columns: LookupColumn[] = useMemo(() => {
    if (lookup?.column_schema && lookup.column_schema.length > 0) {
      return [...lookup.column_schema].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      );
    }
    if (rows.length > 0) {
      const seen = new Set<string>();
      const out: LookupColumn[] = [];
      for (const r of rows) {
        for (const k of Object.keys(r.data ?? {})) {
          if (!seen.has(k)) {
            seen.add(k);
            out.push({ name: k, order: out.length });
          }
        }
      }
      return out;
    }
    if (lookup?.key_column) {
      return [{ name: lookup.key_column, role: "key", order: 0 }];
    }
    return [];
  }, [lookup, rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(
      (r) =>
        JSON.stringify(r.data).toLowerCase().includes(q) ||
        r.key_value_display.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const duplicateDefaultContent = useMemo(() => {
    const defaultRow = rows.find((row) => row.is_default);
    if (!defaultRow) {
      return [] as string[][];
    }
    const byValue = new Map<string, string[]>();
    for (const column of columns) {
      if (column.name === lookup?.key_column) {
        continue;
      }
      const value = String(defaultRow.data[column.name] ?? "").trim();
      if (!value) {
        continue;
      }
      const names = byValue.get(value) ?? [];
      names.push(column.name);
      byValue.set(value, names);
    }
    return Array.from(byValue.values()).filter((names) => names.length > 1);
  }, [columns, lookup?.key_column, rows]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["campaign-lookup", lookupId] });
    qc.invalidateQueries({ queryKey: ["campaign-lookup-rows", lookupId] });
    qc.invalidateQueries({ queryKey: ["campaign-lookup-row-counts"] });
  };

  if (lookupQ.isLoading || rowsQ.isLoading) {
    return (
      <div className="p-8">
        <LoadingSpinner />
      </div>
    );
  }
  if (!lookup) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-800">
          Lookup not found.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <button
        type="button"
        onClick={() => navigate("/campaigns/lookups")}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        Back to Lookups
      </button>

      <LookupHeader lookup={lookup} onUpdated={refresh} />

      {columns.some((column) => column.name.toLowerCase() === "is_default") && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          This lookup contains a content column named <code>is_default</code>.
          That does not mark a default row. Use the star beside the row, or
          export/re-import with the reserved <code>__is_default</code> metadata
          column.
        </div>
      )}

      {duplicateDefaultContent.length > 0 && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-medium">
            The default row repeats identical content across multiple fields.
          </div>
          <p className="mt-1 text-xs">
            Confirm this is intentional before sending. Repeated newsletter
            sections often mean one HTML block was pasted into every column.
          </p>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {duplicateDefaultContent.map((names) => (
              <li key={names.join("|")}>{names.join(", ")}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-md relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search rows…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => downloadCsv(lookup, rows, columns)}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => setShowCsv(true)}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Upload CSV
          </button>
          <AddRowButton lookup={lookup} columns={columns} onAdded={refresh} />
        </div>
      </div>

      {filteredRows.length > 0 && (
        <p className="text-xs text-gray-500 mb-2">
          Tip: click any cell to edit. Changes save automatically when you press
          Enter or click away — you'll see a “Saved ✓” confirmation and a toast.
        </p>
      )}

      {filteredRows.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center text-sm text-gray-600">
          {rows.length === 0
            ? "No rows yet. Click + Add Row or Upload CSV."
            : "No rows match the search."}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 py-2 w-10"></th>
                {columns.map((col) => (
                  <th
                    key={col.name}
                    className={`px-3 py-2 text-left font-medium ${col.name === lookup.key_column ? "bg-blue-50" : ""}`}
                  >
                    <div className="flex items-center gap-1 group">
                      {editingHeader === col.name ? (
                        <ColumnRenameInput
                          lookupId={lookupId}
                          oldName={col.name}
                          onDone={() => {
                            setEditingHeader(null);
                            refresh();
                          }}
                          onCancel={() => setEditingHeader(null)}
                        />
                      ) : (
                        <>
                          <span className="font-mono text-xs">{col.name}</span>
                          {col.name === lookup.key_column && (
                            <span
                              title="Key column"
                              className="text-blue-600 text-xs"
                            >
                              ★
                            </span>
                          )}
                          <button
                            type="button"
                            title="Rename column"
                            onClick={() => setEditingHeader(col.name)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-700"
                          >
                            <PencilSquareIcon className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.map((row) => (
                <RowEditor
                  key={row.id}
                  row={row}
                  columns={columns}
                  keyColumn={lookup.key_column}
                  onChanged={refresh}
                  onSetDefault={async () => {
                    await setLookupDefaultRow(lookupId, row.id);
                    refresh();
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCsv && (
        <LookupCsvUploadModal
          lookupId={lookupId}
          defaultKeyColumn={lookup.key_column}
          existingColumns={columns.map((c) => c.name)}
          onClose={() => setShowCsv(false)}
          onSuccess={() => {
            setShowCsv(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Header — name, description, key column picker
// ============================================================================

function LookupHeader({
  lookup,
  onUpdated,
}: {
  lookup: CampaignLookup;
  onUpdated: () => void;
}) {
  const [name, setName] = useState(lookup.name);
  const [description, setDescription] = useState(lookup.description ?? "");
  const [error, setError] = useState<string | null>(null);

  // Reset local state when the lookup changes (refresh after row update etc.)
  useEffect(() => {
    setName(lookup.name);
    setDescription(lookup.description ?? "");
  }, [lookup.id, lookup.name, lookup.description]);

  const saveHeader = useMutation({
    mutationFn: () =>
      updateLookup(lookup.id, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      setError(null);
      onUpdated();
    },
    onError: (err: Error) => setError(err.message),
  });

  const changeKey = useMutation({
    mutationFn: (newKey: string) => changeLookupKeyColumn(lookup.id, newKey),
    onSuccess: () => {
      setError(null);
      onUpdated();
    },
    onError: (err: Error) => setError(err.message),
  });

  const dirty =
    name.trim() !== lookup.name ||
    (description.trim() || null) !== (lookup.description ?? null);

  return (
    <div className="bg-white border border-gray-200 rounded-md p-6">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full text-lg font-semibold text-gray-900 border-0 border-b border-transparent focus:border-blue-500 focus:outline-none bg-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="(none)"
            className="w-full text-sm text-gray-600 border-0 border-b border-transparent focus:border-blue-500 focus:outline-none bg-transparent"
          />
        </div>

        <div className="flex items-end gap-3 pt-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Key Column
            </label>
            <select
              value={lookup.key_column}
              onChange={(e) => {
                if (
                  confirm(
                    `Change key column to "${e.target.value}"? All row keys will be recomputed. The change is rejected if it would produce duplicate keys.`,
                  )
                ) {
                  changeKey.mutate(e.target.value);
                }
              }}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {lookup.column_schema.map((col) => (
                <option key={col.name} value={col.name}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>
          {dirty && (
            <button
              type="button"
              onClick={() => saveHeader.mutate()}
              disabled={saveHeader.isPending}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saveHeader.isPending ? "Saving…" : "Save"}
            </button>
          )}
        </div>
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>
    </div>
  );
}

// ============================================================================
// Add row button + inline editor
// ============================================================================

function AddRowButton({
  lookup,
  columns,
  onAdded,
}: {
  lookup: CampaignLookup;
  columns: LookupColumn[];
  onAdded: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const addRow = useMutation({
    mutationFn: async () => {
      const data: Record<string, unknown> = {};
      for (const col of columns) {
        data[col.name] = "";
      }
      // Use a placeholder key value — the marketer fills it in inline.
      // The unique index would reject if multiple "" rows existed; in
      // practice the marketer fills the key right after add.
      data[lookup.key_column] = "(new)";
      return insertLookupRow(lookup.id, lookup.key_column, data);
    },
    onSuccess: () => {
      setError(null);
      onAdded();
    },
    onError: (err: Error) => {
      const msg = err.message.includes("idx_campaign_lookup_rows_lookup_key")
        ? `A row with key "(new)" already exists. Edit the existing one before adding another.`
        : err.message;
      setError(msg);
      setTimeout(() => setError(null), 5000);
    },
  });

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => addRow.mutate()}
        disabled={addRow.isPending}
        className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
      >
        <PlusIcon className="h-4 w-4" />
        Add Row
      </button>
      {error && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800 z-10">
          {error}
        </div>
      )}
    </div>
  );
}

function RowEditor({
  row,
  columns,
  keyColumn,
  onChanged,
  onSetDefault,
}: {
  row: CampaignLookupRow;
  columns: LookupColumn[];
  keyColumn: string;
  onChanged: () => void;
  onSetDefault: () => void;
}) {
  return (
    <tr className={row.is_default ? "bg-amber-50" : ""}>
      <td className="px-2 py-2 align-top">
        <button
          type="button"
          title={row.is_default ? "Default row" : "Set as default"}
          onClick={() => {
            if (!row.is_default) {
              onSetDefault();
            }
          }}
          className={`p-1 rounded ${row.is_default ? "text-amber-500" : "text-gray-300 hover:text-amber-400"}`}
        >
          {row.is_default ? (
            <StarFilled className="h-4 w-4" />
          ) : (
            <StarOutline className="h-4 w-4" />
          )}
        </button>
      </td>
      {columns.map((col) => (
        <CellEditor
          key={col.name}
          row={row}
          columnName={col.name}
          isKey={col.name === keyColumn}
          onChanged={onChanged}
        />
      ))}
      <td className="px-2 py-2 align-top">
        <button
          type="button"
          title="Delete row"
          onClick={async () => {
            if (confirm(`Delete row "${row.key_value_display}"?`)) {
              await deleteLookupRow(row.id);
              onChanged();
            }
          }}
          className="p-1 text-gray-400 hover:text-red-600"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function CellEditor({
  row,
  columnName,
  isKey,
  onChanged,
}: {
  row: CampaignLookupRow;
  columnName: string;
  isKey: boolean;
  onChanged: () => void;
}) {
  const { showToast } = useToast();
  const initial = String(row.data[columnName] ?? "");
  const [value, setValue] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved">("idle");

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const save = async () => {
    if (value === initial) {
      setEditing(false);
      return;
    }
    setError(null);
    if (isKey && !value.trim()) {
      setError("Key cannot be blank");
      return;
    }
    setStatus("saving");
    try {
      const newData = { ...row.data, [columnName]: value };
      const patch: Parameters<typeof updateLookupRow>[1] = { data: newData };
      if (isKey) {
        const trimmed = value.trim();
        patch.key_value_normalized = trimmed.toLowerCase();
        patch.key_value_display = trimmed;
      }
      await updateLookupRow(row.id, patch);
      setEditing(false);
      setStatus("saved");
      showToast("success", "Lookup value saved");
      onChanged(); // refetch so the cell shows the persisted server value
      window.setTimeout(() => setStatus("idle"), 2500);
    } catch (err) {
      setStatus("idle");
      const msg = (err as Error).message;
      const friendly = msg.includes("idx_campaign_lookup_rows_lookup_key")
        ? `A row with this key already exists`
        : msg;
      setError(friendly);
      showToast("error", `Save failed: ${friendly}`);
    }
  };

  return (
    <td
      className={`px-3 py-2 align-top ${isKey ? "bg-blue-50" : ""}`}
      onClick={() => !editing && setEditing(true)}
    >
      {editing ? (
        <div className="space-y-1">
          <input
            type="text"
            autoFocus
            value={value}
            disabled={status === "saving"}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                save();
              }
              if (e.key === "Escape") {
                setValue(initial);
                setEditing(false);
                setError(null);
              }
            }}
            className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none disabled:bg-gray-100"
          />
          {status === "saving" && (
            <div className="text-xs text-gray-500">Saving…</div>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
      ) : (
        <span className="flex items-center gap-1.5">
          <span className="block truncate max-w-[300px]">
            {initial || <span className="text-gray-300">—</span>}
          </span>
          {status === "saved" && (
            <span className="text-xs font-medium text-green-600 whitespace-nowrap">
              Saved ✓
            </span>
          )}
        </span>
      )}
    </td>
  );
}

// ============================================================================
// Inline column rename (RPC-backed)
// ============================================================================

function ColumnRenameInput({
  lookupId,
  oldName,
  onDone,
  onCancel,
}: {
  lookupId: string;
  oldName: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(oldName);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const v = value.trim();
    if (!v || v === oldName) {
      onCancel();
      return;
    }
    try {
      await renameLookupColumn(lookupId, oldName, v);
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div>
      <input
        type="text"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={submit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            submit();
          }
          if (e.key === "Escape") {
            onCancel();
          }
        }}
        className="w-32 px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none"
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

// ============================================================================
// CSV download
// ============================================================================

function downloadCsv(
  lookup: CampaignLookup,
  rows: CampaignLookupRow[],
  columns: LookupColumn[],
) {
  const headers = columns.map((c) => c.name);
  const escapeCell = (v: unknown): string => {
    const s = String(v ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const lines = [
    ["__is_default", ...headers].map(escapeCell).join(","),
    ...rows.map((r) =>
      [r.is_default ? "true" : "false", ...headers.map((h) => r.data[h])]
        .map(escapeCell)
        .join(","),
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${lookup.name.replace(/[^a-z0-9]+/gi, "_")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
